import { useState, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import ProductFormSheet from "@/components/ProductFormSheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as db from "@/lib/db";

const ProductCostSettings = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [p, c, u] = await Promise.all([db.listProducts(), db.listCategories(), db.listUnits()]);
      setProducts(p);
      setCategories(c);
      setUnits(u);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  const handleFormSuccess = () => {
    fetchData();
    handleFormClose();
  };

  const handleToggleStatus = async (productId) => {
    try {
      const response = await db.toggleProductStatus(productId);
      const statusText = response.isActive ? "activated" : "deactivated";
      toast.success(`Product ${statusText} successfully`);
      fetchData();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to update product status");
    }
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    const matchesStatus = selectedStatus === "all" || 
      (selectedStatus === "active" && product.isActive) ||
      (selectedStatus === "inactive" && !product.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Group products by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Page Header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="page-title">
              Product Cost Settings
            </h1>
            <p className="text-sm text-slate-600 mt-1">Manage your products, costs & pricing</p>
          </div>
          <Button
            onClick={handleAddProduct}
            className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full font-semibold h-12 px-6 transition-transform active:scale-95"
            data-testid="add-product-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters and Search */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm" data-testid="filters-section">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by product name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 pl-12 pr-4 text-base w-full"
                data-testid="search-input"
              />
            </div>

            {/* Category and Status Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">Status</label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl">
            <p className="text-slate-600">No products found</p>
            <p className="text-sm text-slate-400 mt-2">Add your first product to get started</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedProducts).map((categoryName) => (
              <div key={categoryName} data-testid={`category-group-${categoryName}`}>
                <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {categoryName}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groupedProducts[categoryName].map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onEdit={handleEditProduct}
                      onToggleStatus={handleToggleStatus}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Product Form Sheet */}
      <ProductFormSheet
        open={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        product={editingProduct}
        categories={categories}
        units={units}
        onCategoriesUpdate={fetchData}
        onUnitsUpdate={fetchData}
      />
    </div>
  );
};

export default ProductCostSettings;